/**
 * useImageGenerationFlow Hook
 *
 * Orchestrates the image generation flow for Solomon.
 * Manages context, prompt building, URL validation, and timeout state.
 */

import { useState, useCallback } from 'react';

import { useGenerationTimeout } from './useGenerationTimeout';
import { isValidImageUrl } from '../utils/imageContextValidator';
import { buildImageGenerationPrompt, type ImageGenerationContext } from '../utils/promptBuilder';

/**
 * State returned by the useImageGenerationFlow hook
 */
export interface ImageGenerationFlowState {
  /** Current generation context (null if not generating) */
  context: ImageGenerationContext | null;
  /** Generated prompt for the current context (null if not generating) */
  prompt: string | null;
  /** Whether the generation has timed out */
  isTimedOut: boolean;
  /** Open the panel for generation with the given context */
  openForGeneration: (context: ImageGenerationContext) => void;
  /** Clear the generation state and reset timeout */
  clearGeneration: () => void;
}

/**
 * Hook to orchestrate the image generation flow.
 *
 * Combines prompt building, URL validation, and timeout management
 * into a single cohesive flow for generating images with Solomon.
 *
 * @param artistName - The name of the artist for prompt personalization
 * @returns Generation flow state and control functions
 *
 * @example
 * const { context, prompt, isTimedOut, openForGeneration, clearGeneration } =
 *   useImageGenerationFlow(artistName);
 *
 * // Open panel for vertical image generation
 * openForGeneration({
 *   imageKey: 'vertical',
 *   aspectRatio: '9:16',
 *   fieldName: 'profile_vertical_image_url',
 *   displayName: 'vertical profile image (9:16)',
 *   existingImageUrl: artist.profile_vertical_image_url,
 * });
 */
export function useImageGenerationFlow(artistName: string): ImageGenerationFlowState {
  const [context, setContext] = useState<ImageGenerationContext | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);

  const { isTimedOut, startTimeout, resetTimeout } = useGenerationTimeout({
    timeoutMs: 120000, // 2 minutes
  });

  const openForGeneration = useCallback(
    (ctx: ImageGenerationContext) => {
      // Validate URL - only accept URLs from trusted CDN domains
      const validatedUrl = isValidImageUrl(ctx.existingImageUrl) ? ctx.existingImageUrl : undefined;

      const safeContext: ImageGenerationContext = {
        ...ctx,
        existingImageUrl: validatedUrl,
      };

      setContext(safeContext);

      const generatedPrompt = buildImageGenerationPrompt({
        artistName,
        context: safeContext,
        hasReferenceImage: !!validatedUrl,
      });

      setPrompt(generatedPrompt);
      startTimeout();
    },
    [artistName, startTimeout]
  );

  const clearGeneration = useCallback(() => {
    setContext(null);
    setPrompt(null);
    resetTimeout();
  }, [resetTimeout]);

  return {
    context,
    prompt,
    isTimedOut,
    openForGeneration,
    clearGeneration,
  };
}

export default useImageGenerationFlow;
